import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button, Card } from "./ui";
import Notificacoes from "./Notificacoes";
import BuscaRapida from "./BuscaRapida";
import { SECOES } from "./navegacao";
import { IconeBusca, IconeMais } from "./icones";

const PAPEL_LABEL = {
    proprietario: "Proprietário",
    associado: "Associado",
    estagiario: "Estagiário"
};

const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { usuario, ehProprietario, logout, logoEscritorio } = useAuth();
    const [menuAberto, setMenuAberto] = useState(false);
    const [maisAberto, setMaisAberto] = useState(false);
    const [buscaAberta, setBuscaAberta] = useState(false);
    const menuRef = useRef(null);
    const maisRef = useRef(null);

    // Atalho global Ctrl+K / ⌘K para a busca
    useEffect(() => {
        const aoTeclar = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setBuscaAberta(true);
            }
        };
        window.addEventListener("keydown", aoTeclar);
        return () => window.removeEventListener("keydown", aoTeclar);
    }, []);

    // Fecha menus ao clicar fora
    useEffect(() => {
        const aoClicar = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuAberto(false);
            if (maisRef.current && !maisRef.current.contains(e.target)) setMaisAberto(false);
        };
        document.addEventListener("mousedown", aoClicar);
        return () => document.removeEventListener("mousedown", aoClicar);
    }, []);

    const sair = () => {
        logout();
        navigate("/login", { replace: true });
    };

    const ativo = (rota) => location.pathname === rota;

    // Barra inferior (mobile): seções principais + "Mais" para o resto
    const principais = SECOES.filter((s) => s.principal);
    const extras = SECOES.filter((s) => !s.principal);

    return (
        <>
            <nav className="nav">
                <div className="nav-topo">
                    <span className="nav-brand">
                        <img src={logoEscritorio} alt={`Logo de ${usuario?.nome_escritorio || "escritório"}`} />
                        <span className="nav-brand-nome">{usuario?.nome_escritorio || "Selo Jurídico"}</span>
                    </span>

                    <div className="nav-acoes">
                        <button className="busca-gatilho-icone" onClick={() => setBuscaAberta(true)} aria-label="Buscar (Ctrl K)">
                            <IconeBusca />
                        </button>

                        <Notificacoes />

                        <div ref={menuRef} style={{ position: "relative" }}>
                            <button className="nav-avatar" aria-label="Menu do usuário" onClick={() => setMenuAberto((v) => !v)}>
                                {(usuario?.nome || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
                            </button>
                            {menuAberto && (
                                <Card className="elev-md" style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", minWidth: 210, padding: 8, gap: 2, zIndex: 25 }}>
                                    <span className="text-muted" style={{ fontSize: 13, padding: "6px 8px" }}>
                                        {usuario?.nome} · {PAPEL_LABEL[usuario?.papel] || ""}
                                    </span>
                                    <Button variante="ghost" style={{ justifyContent: "flex-start" }}
                                        onClick={() => { setMenuAberto(false); navigate("/assinatura"); }}>Assinatura</Button>
                                    <Button variante="ghost" style={{ justifyContent: "flex-start" }}
                                        onClick={() => { setMenuAberto(false); navigate("/configuracoes"); }}>Configurações</Button>
                                    {ehProprietario && (
                                        <Button variante="ghost" style={{ justifyContent: "flex-start" }}
                                            onClick={() => { setMenuAberto(false); navigate("/equipe"); }}>Equipe</Button>
                                    )}
                                    <Button variante="ghost" style={{ justifyContent: "flex-start" }} onClick={sair}>Sair</Button>
                                </Card>
                            )}
                        </div>
                    </div>
                </div>

                {/* Pílula central com as seções (escondida no mobile — ver .nav-pilula) */}
                <div className="nav-pilula-wrap">
                    <div className="nav-pilula">
                        {SECOES.map(({ rota, rotulo, Icone }) => (
                            <button
                                key={rota}
                                type="button"
                                className={ativo(rota) ? "ativo" : ""}
                                aria-current={ativo(rota) ? "page" : undefined}
                                onClick={() => navigate(rota)}
                            >
                                <Icone /> <span>{rotulo}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </nav>

            {/* Barra inferior — só no mobile (ver .nav-inferior no CSS) */}
            <nav className="nav-inferior">
                {principais.map(({ rota, curto, Icone }) => (
                    <button key={rota} className={ativo(rota) ? "ativo" : ""} onClick={() => navigate(rota)}>
                        <Icone /><span>{curto}</span>
                    </button>
                ))}
                <div ref={maisRef} style={{ position: "relative" }}>
                    <button className={extras.some((s) => ativo(s.rota)) ? "ativo" : ""} onClick={() => setMaisAberto((v) => !v)}>
                        <IconeMais /><span>Mais</span>
                    </button>
                    {maisAberto && (
                        <Card className="elev-lg nav-mais-menu">
                            {extras.map(({ rota, rotulo, Icone }) => (
                                <Button key={rota} variante="ghost" style={{ justifyContent: "flex-start" }}
                                    onClick={() => { setMaisAberto(false); navigate(rota); }}>
                                    <Icone /> {rotulo}
                                </Button>
                            ))}
                            <Button variante="ghost" style={{ justifyContent: "flex-start" }}
                                onClick={() => { setMaisAberto(false); navigate("/assinatura"); }}>Assinatura</Button>
                            <Button variante="ghost" style={{ justifyContent: "flex-start" }}
                                onClick={() => { setMaisAberto(false); navigate("/configuracoes"); }}>Configurações</Button>
                            {ehProprietario && (
                                <Button variante="ghost" style={{ justifyContent: "flex-start" }}
                                    onClick={() => { setMaisAberto(false); navigate("/equipe"); }}>Equipe</Button>
                            )}
                        </Card>
                    )}
                </div>
            </nav>

            <BuscaRapida aberto={buscaAberta} onFechar={() => setBuscaAberta(false)} />
        </>
    );
};

export default Header;
